import { useQuery } from "@tanstack/react-query";
import {
  type ReactNode,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { ChevronDownIcon, DownloadIcon, Link2OffIcon, Maximize2Icon, PlusIcon } from "lucide-react";
import { toast } from "sonner";
import { fetchApi, fetchApiFileBlob } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useTheme } from "@/contexts/theme-context";
import { useProfileData } from "@/hooks/use-profile-data";
import {
  createHeadline,
  createIssue,
  createTodo,
  getAttendees,
} from "@/api/l10";
import { ITEM_IDENTITY } from "@/components/l10/l10-theme";
import { useBroadcastTeams, useTargetTeams } from "@/components/l10/headlines/use-broadcast-teams";
import {
  draftError,
  draftForTypeSwitch,
  emptyDraft,
  type ItemDraft,
  type MeetingItemType,
} from "@/components/l10/composer/meeting-item";
import { ItemSurfaceCreate } from "@/components/l10/item-surface/item-surface";

// Shape the /kory/artifacts/{token} endpoint returns.
export interface KoryArtifactView {
  id: string;
  title: string;
  filename: string;
  content_type: string;
  is_html: boolean;
  // HTML: the composed document (Hickory stylesheet + shell + markup).
  // Other types: the raw file text for download.
  content: string;
  shared_by: string;
  updated_at: string;
  is_owner: boolean;
  // Owner-only: the original generation prompt Kory wrote at publish time.
  generation_prompt: string | null;
  // The composer doc-type hint + theme pack. Both OPTIONAL: the column / API
  // field lands in a separate PR, so until that ships these are simply absent
  // and consumers must treat absence the same as "untyped" (render no
  // per-type chrome). Free-form strings, not a hard enum — unknown values
  // fall through to the generic frame.
  doc_type?: string | null;
  theme_pack?: string | null;
  // Internal-link access policy ('org_link' | 'people' | 'sensitive'). Owner-only
  // UI uses it to label the link's reach; absent on legacy responses → 'org_link'.
  access_mode?: string | null;
  // How to render this artifact: 'html' (sandboxed iframe via `content`), 'pdf'
  // (react-pdf), 'spreadsheet' (interactive grid), 'image' (<img>), or
  // 'download'. Absent on legacy responses → treat as 'html' when is_html, else
  // 'download'.
  render_kind?: "html" | "pdf" | "spreadsheet" | "image" | "download" | null;
  // Same-origin API path to the raw bytes (Content-Type-correct, binary-safe).
  // Set for every non-HTML artifact; the pdf/spreadsheet viewers + the download
  // fall back fetch from here instead of the (UTF-8-mangled) `content` string.
  content_url?: string | null;
}

// Heavy, type-specific viewers — code-split so react-pdf / exceljs only load
// when a PDF / spreadsheet artifact is actually opened.
const PdfArtifactViewer = lazy(() => import("./pdf-artifact-viewer"));
const SheetArtifactViewer = lazy(() => import("./sheet-artifact-viewer"));
const ImageArtifactViewer = lazy(() => import("./image-artifact-viewer"));
// The full-document viewer modal wraps THIS component's core; the preview mode
// opens it on click. Lazy so the dependency is dynamic-only — a static import
// here would form a module cycle with artifact-viewer-modal (which imports
// KoryArtifactFrame), which Vite HMR handles poorly.
const ArtifactViewerModal = lazy(() =>
  import("./artifact-viewer-modal").then((m) => ({ default: m.ArtifactViewerModal })),
);

function ViewerFallback() {
  return (
    <div className="flex h-full items-center justify-center">
      <Skeleton className="h-[70vh] w-[min(100%,720px)]" />
    </div>
  );
}

// ─── DEAD PATH — the `<kory-action>` embedded-button bridge ─────────────────
// Product has declared this path dead: artifacts no longer need to inject a
// "push this into an L10" button, because the composer now resolves its own
// destination (the home-L10 preselect on ``draft.targetTeamId`` + the native
// "Create in"/"Post in" picker), the "＋ Create ▾" button covers create-from-
// artifact on every artifact surface, and Drive gatekeeps recurring pushes.
// It is NOT yet safe to delete: live guidance still AUTHORS these buttons —
// the mcp-gateway ``write_artifact`` tool guidance (mcp-gateway/tools/
// artifacts.py), the ai-team-project-update skill's "Make Discussion items
// actionable" section, and the morning-board gallery exemplar — and legacy
// prod artifacts carry them; removing the host handler would strand those
// buttons disabled-on-click (the bootstrap waits for a ``kory.action.result``
// reply). Once the authoring guidance is retired, delete: this type, the
// ``kory.action`` handling in ``handleLoad`` below, ``kindToType``, and the
// synthesized-PendingAction shim in ``CreateFromArtifactButton`` (give it a
// direct prop path into ``ArtifactItemComposerHost`` instead). Do not extend.
//
// A typed action request the artifact's bootstrap posts over the MessagePort.
// The artifact only NAMES a verb + args (never an endpoint or any authority);
// the host re-validates, re-resolves the target, confirms, and executes.
// `reply` posts the outcome back so the in-artifact button reflects the real
// result instead of an optimistic guess.
interface PendingAction {
  id: string;
  verb: string;
  payload: { kind?: string; title?: string; owner_hint?: string; body?: string };
  reply: (res: { ok: boolean; label?: string }) => void;
}

/**
 * Fetch a Kory Artifact by its secret token. Theme-aware: the artifact is its
 * own document and can't read HickoryOS's `dark` class, so we pass `?theme=`
 * and include it in the query key, re-fetching when the toggle flips. Deduped
 * by react-query — multiple consumers of the same token share one request.
 */
export function useKoryArtifact(token: string | undefined) {
  const { isDarkMode } = useTheme();
  const theme = isDarkMode ? "dark" : "light";
  return useQuery<KoryArtifactView>({
    queryKey: ["kory", "artifact", token, theme],
    queryFn: () =>
      fetchApi(`/kory/artifacts/${token}?theme=${theme}`) as Promise<KoryArtifactView>,
    enabled: Boolean(token),
    retry: false,
    // The backend 404s an unknown/revoked token by design (never an existence
    // oracle) and every caller already renders that as the graceful "isn't
    // available" state below — tell the global onError not to report it.
    meta: { expectedErrorStatuses: [404] },
  });
}

/**
 * Renders a Kory Artifact's body — a sandboxed iframe for HTML, a download
 * prompt otherwise. Generic: drop it into any container (roadmap lightbox,
 * issue / headline / to-do detail, the share page). Page chrome — title bar,
 * action buttons — is the caller's responsibility.
 *
 * This is the SHARED viewer core: the full-document viewer modal
 * ({@link ArtifactViewerModal}) wraps it in Dialog/Drawer chrome, and the inline
 * `mode="preview"` variant (used by {@link ArtifactBody} as a composer body)
 * renders the SAME iframe/pdf/spreadsheet render path scaled down. One render
 * path, two presentation modes — so any render-side feature added here
 * (scroll-to, the `<kory-action>` bridge, the type-specific viewers) applies to
 * both and can't drift.
 *
 * - `mode="full"` (default) — the frame fills its slot at native size (the modal
 *   / share page / roadmap lightbox).
 * - `mode="preview"` — a scaled-down thumbnail clipped to the parent-sized slot,
 *   with a passive bottom feather and a hover-reveal "Open full document" button;
 *   the whole surface is clickable and opens the full viewer modal. The PARENT
 *   sizes the slot (the preview never sets its own height).
 *
 * When `actionsEnabled` is set, this frame also acts as an MCP-Apps-style
 * HOST: it hands the artifact one end of a MessageChannel after load, and
 * executes the typed actions the artifact's `<kory-action>` buttons request
 * (e.g. "push this issue into an L10"). Both the roadmap lightbox and the
 * authenticated share page turn it on; leave it off in any context where the
 * viewer shouldn't be able to act (then those buttons stay inert there).
 * NB: the `<kory-action>` action path is a DEAD PATH kept for legacy artifacts
 * only — see the block comment on ``PendingAction``.
 */
export function KoryArtifactFrame({
  token,
  className,
  actionsEnabled = false,
  initialSection,
  mode = "full",
  naturalWidth = 800,
  previewTitle,
  harnessSrcDoc,
}: {
  token: string | undefined;
  className?: string;
  actionsEnabled?: boolean;
  // A subsection id (a Drive proposal's dedup_key) to deep-link to. On load the
  // host posts a `kory.scroll-to` message into the artifact so its document can
  // scroll/highlight the matching `[data-dedup-key]` node. Fire-and-forget: an
  // artifact that doesn't listen simply shows the whole document.
  initialSection?: string;
  // Presentation mode — `full` (native size, the caller supplies chrome) or
  // `preview` (scaled thumbnail clipped to the parent slot; click opens the full
  // viewer modal). Defaults to `full` so every existing caller is unchanged.
  mode?: "preview" | "full";
  // Preview only — the natural page width the artifact document lays out at; the
  // preview renders the frame at this width and CSS-scales it to fit the
  // container. ~760–800 matches the Hickory artifact stylesheet's page width.
  naturalWidth?: number;
  // Preview only — the a11y title for the full viewer modal opened on click.
  previewTitle?: string;
  // Harness-only escape hatch (screenshot devharness, no backend): a static HTML
  // document rendered in the sandboxed iframe INSTEAD of fetching the artifact by
  // token. Feeds the SAME render path so the preview visual is faithful. Never
  // set in app code.
  harnessSrcDoc?: string;
}) {
  const fetched = useKoryArtifact(harnessSrcDoc ? undefined : token);
  // In the harness, synthesize a minimal HTML artifact view from the static
  // srcDoc so the render path below is identical to the real one.
  const data: KoryArtifactView | undefined = harnessSrcDoc
    ? ({ is_html: true, content: harnessSrcDoc, title: previewTitle ?? "" } as KoryArtifactView)
    : fetched.data;
  const isLoading = harnessSrcDoc ? false : fetched.isLoading;
  const isError = harnessSrcDoc ? false : fetched.isError;
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const portRef = useRef<MessagePort | null>(null);
  const [pending, setPending] = useState<PendingAction | null>(null);

  useEffect(() => () => portRef.current?.close(), []);

  // Hand the artifact one end of a MessageChannel once it loads. The port IS
  // the capability: only the document we handed port2 to can post back to us,
  // so the channel needs no origin check (the iframe's opaque "null" origin
  // couldn't be verified anyway). We still validate message shape + verb
  // before acting, and the artifact can only NAME a verb — never execute one.
  const handleLoad = () => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    // Deep-link to a subsection (sent regardless of actionsEnabled — the unfurl
    // modal opens with actions OFF but still wants the scroll). Fire-and-forget.
    if (initialSection) {
      win.postMessage({ type: "kory.scroll-to", section: initialSection }, "*");
    }
    if (!actionsEnabled) return;
    portRef.current?.close();
    const channel = new MessageChannel();
    portRef.current = channel.port1;
    const port = channel.port1;
    port.onmessage = (e: MessageEvent) => {
      const d = e.data as Record<string, unknown> | null;
      // DEAD PATH — ``kory.action`` handling below serves only legacy
      // `<kory-action>` buttons; see the block comment on ``PendingAction``.
      // The MessageChannel itself stays (other message types may ride it).
      if (!d || d.type !== "kory.action" || typeof d.id !== "string") return;
      const id = d.id;
      const reply = (res: { ok: boolean; label?: string }) =>
        port.postMessage({ type: "kory.action.result", id, ok: res.ok, label: res.label });
      // Verb allowlist: only known verbs open a dialog; anything else is
      // declined immediately so the button re-enables.
      if (d.verb !== "l10.add_item") {
        reply({ ok: false });
        return;
      }
      setPending({
        id,
        verb: "l10.add_item",
        payload: (d.payload as PendingAction["payload"]) ?? {},
        reply,
      });
    };
    win.postMessage("kory:host-init", "*", [channel.port2]);
  };

  // The raw render path — IDENTICAL for `full` and `preview` (the preview shell
  // below just scales + clips it). Computed as a node (not early-returned) so
  // the preview wrapper can consume it while keeping hook order unconditional.
  let frameBody: ReactNode;
  if (isLoading) {
    frameBody = (
      <div className="space-y-4 p-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  } else if (isError || !data) {
    frameBody = (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
        <Link2OffIcon className="h-6 w-6 text-muted-foreground" />
        <p className="text-sm font-medium">This artifact isn't available</p>
        <p className="text-xs text-muted-foreground">
          The link may have been revoked, or you may not have access.
        </p>
      </div>
    );
  } else if (data.render_kind === "pdf" && data.content_url) {
    // Type-specific viewers for binary artifacts. Both read the bytes from the
    // access-gated, binary-safe raw endpoint (content_url) — never the JSON
    // `content` string, which UTF-8-mangles binary. Lazily loaded so their heavy
    // deps (react-pdf / exceljs) stay out of the main bundle.
    frameBody = (
      <Suspense fallback={<ViewerFallback />}>
        <PdfArtifactViewer contentUrl={data.content_url} filename={data.filename} />
      </Suspense>
    );
  } else if (data.render_kind === "spreadsheet" && data.content_url) {
    frameBody = (
      <Suspense fallback={<ViewerFallback />}>
        <SheetArtifactViewer contentUrl={data.content_url} filename={data.filename} />
      </Suspense>
    );
  } else if (data.render_kind === "image" && data.content_url) {
    frameBody = (
      <Suspense fallback={<ViewerFallback />}>
        <ImageArtifactViewer contentUrl={data.content_url} filename={data.filename} />
      </Suspense>
    );
  } else if (!data.is_html) {
    frameBody = <ArtifactDownload data={data} />;
  } else {
    // Untrusted artifact HTML. sandbox WITHOUT allow-same-origin gives the
    // document an opaque origin — its scripts cannot touch this HickoryOS page.
    // srcDoc means the markup never has a navigable text/html URL of its own.
    //
    //   allow-scripts                    — artifacts are interactive (charts, etc.)
    //   allow-popups                     — `<a target="_blank">` can open new tabs
    //   allow-popups-to-escape-sandbox   — those new tabs are normal pages
    //
    // Still blocked: allow-same-origin (opaque origin isolates scripts from this
    // page's cookies/DOM) and allow-top-navigation (links can't hijack the
    // parent). Without allow-popups, links in an artifact body would be styled
    // but silently dead — mirror the legacy HtmlFrame sandbox so they open.
    frameBody = (
      <>
        <iframe
          ref={iframeRef}
          onLoad={handleLoad}
          title={data.title}
          srcDoc={data.content}
          sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
          className={className ?? "h-full w-full border-0"}
          // Capture anchor for the playwrighter thumbnail job — the backend's
          // clip_selector targets this (see app/services/kory_thumbnails.py).
          data-testid="artifact-frame"
        />
        {pending && (
          <ArtifactItemComposerHost
            pending={pending}
            artifactTitle={data.title}
            onClose={() => setPending(null)}
          />
        )}
      </>
    );
  }

  if (mode === "preview") {
    return (
      <ArtifactPreviewShell
        token={token}
        title={previewTitle ?? data?.title ?? ""}
        initialSection={initialSection}
        naturalWidth={naturalWidth}
        harnessSrcDoc={harnessSrcDoc}
      >
        {frameBody}
      </ArtifactPreviewShell>
    );
  }

  return frameBody;
}

/**
 * Inline `preview` presentation for {@link KoryArtifactFrame}. Renders the SAME
 * frame body at its natural page width, then CSS-`transform: scale`s it down to
 * fit the container (scale = containerWidth / naturalWidth, tracked with a
 * ResizeObserver), top-aligned and `overflow:hidden` so it clips to the
 * parent-provided height. A passive bottom feather fades the clipped edge; on
 * hover a centered "⤢ Open full document" button appears and the WHOLE surface
 * is clickable → opens the full {@link ArtifactViewerModal}. The shell fills its
 * slot and never sets its own height — the parent sizes it.
 */
function ArtifactPreviewShell({
  token,
  title,
  initialSection,
  naturalWidth,
  harnessSrcDoc,
  children,
}: {
  token: string | undefined;
  title: string;
  initialSection?: string;
  naturalWidth: number;
  harnessSrcDoc?: string;
  children: ReactNode;
}) {
  const containerRef = useRef<HTMLButtonElement | null>(null);
  const [scale, setScale] = useState(1);
  // ``measured`` gates the fade-in: if the slot mounts at 0 width (a drawer
  // still animating open, an unresolved ``flex-1``), the first measure can't set
  // a real scale — we keep the frame hidden until the ResizeObserver reports a
  // real width, so the preview never flashes full-size or sits blank at scale 0.
  const [measured, setMeasured] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Track the container width and scale the natural-width page down to fit.
  // useLayoutEffect so the first paint is already scaled (no full-size flash).
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      if (w > 0) {
        setScale(Math.min(1, w / naturalWidth));
        setMeasured(true);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [naturalWidth]);

  return (
    <>
      <button
        type="button"
        ref={containerRef}
        onClick={() => setModalOpen(true)}
        aria-label="Open full document"
        // ``h-full`` fills a definite-height parent; ``flex-1 min-h-0`` fills a
        // flex-column parent (the ItemTray body slot) even without a definite
        // height — so the preview never collapses regardless of how the parent
        // sizes its slot. ``self-stretch`` fills the cross axis (width).
        className="group relative block h-full min-h-0 w-full flex-1 self-stretch overflow-hidden rounded-md border border-input bg-background text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        {/* The frame at NATURAL width, scaled to fit and pinned top-left. Its own
            height is left to the content; the container clips it. `pointer-events-none`
            + `inert` so clicks/scroll land on the button surface (open-full) and
            keyboard focus can't Tab into the sandboxed iframe — the preview is
            purely visual; the button is the one interactive surface. */}
        <div
          inert
          className={cn(
            "pointer-events-none absolute left-0 top-0 origin-top-left transition-opacity",
            measured ? "opacity-100" : "opacity-0",
          )}
          style={{ width: naturalWidth, height: naturalWidth * 2, transform: `scale(${scale})` }}
        >
          {children}
        </div>
        {/* Passive bottom feather — fades the clipped edge into the surface. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-background to-transparent"
        />
        {/* Hover-reveal centered call-to-action. */}
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute inset-0 flex items-center justify-center",
            "bg-background/0 opacity-0 transition-opacity",
            "group-hover:bg-background/40 group-hover:opacity-100 group-focus-visible:opacity-100",
          )}
        >
          <span className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-3 py-1.5 text-xs font-semibold text-background shadow-sm">
            <Maximize2Icon className="h-3.5 w-3.5" />
            Open full document
          </span>
        </div>
      </button>
      {/* The full viewer — the SAME viewer core, wrapped in modal chrome. Not
          mounted in the harness (no backend to fetch the full doc). Lazy (see
          the const above), so it rides its own chunk; no visible fallback —
          the modal simply appears when the chunk lands. */}
      {!harnessSrcDoc && (
        <Suspense fallback={null}>
          <ArtifactViewerModal
            token={token}
            open={modalOpen}
            onOpenChange={setModalOpen}
            title={title}
            initialSection={initialSection}
          />
        </Suspense>
      )}
    </>
  );
}

/** Map a `<kory-action kind=…>` to a composer type. Unknown / absent → issue
 *  (the hub); the user can still switch the type in the composer. */
function kindToType(kind?: string): MeetingItemType {
  if (kind === "headline") return "headline";
  if (kind === "todo") return "todo";
  return "issue";
}

/**
 * ArtifactItemComposerHost — a HEADLESS ADAPTER that lets an artifact create a
 * Headline / Issue / To-Do through Hickory's NATIVE unified item composer (the
 * same {@link ItemSurfaceCreate} surface the L10 runner + prep board use). It
 * owns ONLY the artifact-specific plumbing; every visible control comes from
 * ``ItemSurfaceCreate`` / ``ItemFields``.
 *
 * Its three adapter jobs:
 *  (a) CONTEXT ADAPTATION — an artifact has no ambient meeting, so this supplies
 *      what a meeting normally would: the accessible-team lists (via the same
 *      server-gated ``useTargetTeams`` / ``useBroadcastTeams`` the meeting
 *      composer uses), the per-team roster fetch (``getAttendees``) that scopes
 *      the owner picker, and the stale-cross-team-owner guard
 *      (``ownerConfirmedOnRoster``) that keeps a to-do unsubmittable while a
 *      newly-picked team's roster is still settling. The destination lives on
 *      ``draft.targetTeamId`` — exactly a standalone create.
 *  (b) The `<kory-action>` IFRAME REPLY PROTOCOL — DEAD PATH, legacy-only (see
 *      the block comment on ``PendingAction``): a ``PendingAction`` (verb +
 *      server-resolved seed: title / body / kind / owner hint) comes in; on
 *      success we ``pending.reply({ ok, label })`` so the in-artifact button
 *      reflects the real outcome. ``CreateFromArtifactButton`` — the LIVE
 *      entry point — reuses this host by synthesizing a ``PendingAction`` with
 *      a NO-OP reply (there's no in-artifact button to answer); when the dead
 *      bridge is deleted, collapse that shim into direct props.
 *  (c) SUBMIT — maps the draft onto the real ``@/api/l10`` create call under the
 *      user's own session, seeding ``artifact_token`` / ``artifact_id`` onto the
 *      draft (and thus the created row) so the item durably wears the artifact.
 *      The artifact never sees a team id, an endpoint, or anything beyond ok/label.
 *
 * INVARIANT: this component renders NOTHING bespoke — all visible UI (title,
 * type switcher, destination picker, owner, per-type fields, notes/artifact tab,
 * footer) is ``ItemSurfaceCreate`` / ``ItemFields``. Do not bolt UI onto it.
 */
// Exported so non-artifact host surfaces (e.g. the Release Hub's
// per-update detail page) can seed the same native composer from their
// own content — pass a synthetic `PendingAction` and leave
// `artifactToken`/`artifactId` unset. The composer already handles the
// no-artifact path (the legacy `<kory-action>` bridge doesn't set one).
export function ArtifactItemComposerHost({
  pending,
  artifactTitle,
  artifactToken,
  artifactId,
  onClose,
}: {
  pending: PendingAction;
  artifactTitle: string;
  /** When set, the artifact renders as the composer BODY (in place of Notes),
   *  compacts the to-do field row, AND is persisted onto the created item (via
   *  ``draft.artifact_token``/``artifact_id`` → the create call). Passed on the
   *  create-from-artifact entry point; the legacy `<kory-action>` bridge leaves
   *  it unset. */
  artifactToken?: string;
  artifactId?: string;
  onClose: () => void;
}) {
  // Seed once from the action payload. Option C: the prefill is the
  // server-resolved artifact, not authority baked into the button.
  //  - explicit ``title`` attr  → the item title; the element's inner text
  //    (``body``) becomes Notes.
  //  - no ``title`` attr        → the inner text IS the title (the author
  //    wrote the item text inside the element); no Notes.
  //  - neither                  → fall back to the artifact's own title.
  const [draft, setDraft] = useState<ItemDraft>(() => {
    const explicitTitle = (pending.payload.title || "").trim();
    const bodyText = (pending.payload.body || "").trim();
    return emptyDraft(kindToType(pending.payload.kind), {
      title: explicitTitle || bodyText || artifactTitle.trim(),
      description: explicitTitle ? bodyText : "",
      // Attach the source artifact so it renders as the body AND persists onto
      // the created item (carried across type switches by draftForTypeSwitch).
      artifact_token: artifactToken,
      artifact_id: artifactId,
    });
  });
  const [saving, setSaving] = useState(false);

  // "Me" — the same default-to-current-user the runner/prep composers use, so
  // a fresh item is mine by default everywhere (not just in the runner).
  const myProfileId = useProfileData().data?.profile_id ?? null;

  // The SAME accessible-team lists the native meeting composer feeds ItemFields
  // (server-gated, so the picker never offers a team the backend would 403 on):
  //  - ``targetTeams`` backs the to-do/issue "Create in" picker (write
  //    ``draft.targetTeamId``); ``defaultId`` is the preselect (home L10 → sole
  //    team → none).
  //  - ``broadcastTeams`` backs the headline "Post in" picker (the composing
  //    team, ``draft.targetTeamId``, is the pinned primary).
  // There's no ambient meeting here, so the destination lives entirely on
  // ``draft.targetTeamId`` — exactly a standalone create.
  const { teams: targetTeams, defaultId: initialTargetId } = useTargetTeams(null);
  const teamId = draft.targetTeamId;
  const broadcastTeams = useBroadcastTeams(teamId);
  // The destination the pickers seed from — the current pick if any, else the
  // preselect (home L10 → sole team → none). Passed as ``defaultTargetId`` so
  // the headline "Post in" picker pins the CURRENT destination as its composing
  // team (the to-do/issue "Create in" pickers drive off ``draft.targetTeamId``
  // directly and are unaffected), keeping the pinned primary and ``submit()``'s
  // ``team_id`` in lockstep after a type switch.
  const defaultTargetId = teamId || initialTargetId;

  // Preselect the destination onto the draft (home L10 → sole team → none). Once
  // the user picks a team the seed is done — never re-impose a default.
  const seededTargetRef = useRef(false);
  useEffect(() => {
    if (!seededTargetRef.current && !teamId && initialTargetId) {
      seededTargetRef.current = true;
      setDraft((d) => ({ ...d, targetTeamId: initialTargetId }));
    }
  }, [initialTargetId, teamId]);

  // The owner picker scopes to the chosen team's roster.
  const { data: attendees, isFetching: rosterFetching } = useQuery({
    queryKey: ["l10", "attendees", teamId],
    queryFn: () => getAttendees(teamId),
    enabled: Boolean(teamId),
  });

  // The selected owner is only trustworthy once the chosen team's roster has
  // loaded and confirms membership. Between picking a new destination team and
  // its roster settling, a stale owner from the prior team is still in the
  // draft — gate on this so a to-do can't be submitted with that cross-team
  // owner during the load window.
  const ownerConfirmedOnRoster =
    !draft.ownerProfileId ||
    (!rosterFetching && !!attendees?.some((a) => a.id === draft.ownerProfileId));

  // Resolve the owner ONCE per chosen team, after its roster loads:
  //   explicit owner-hint match → me (if I'm on this team) → unassigned.
  // One coherent step (reset per destination ``teamId``) that also prunes a
  // stale owner carried over from a previous destination team. The ref records
  // what we resolved to (``null`` until then) so a manual pick/clear afterward
  // isn't clobbered; a hint is matched on display name (exact first, then
  // substring) so "Spencer" → "Spencer Davis".
  //
  // The ref holds the RESOLVED VALUE, not just a "done" flag: after we resolve,
  // the effective owner for this team is what we set, even before the setDraft
  // commits. React Strict Mode invokes this effect twice on the same snapshot;
  // reading the resolved value (not the uncommitted draft) keeps the second
  // pass idempotent — without it, the second pass would see the still-stale
  // pre-resolution owner and prune the value the first pass just queued.
  const ownerResolvedRef = useRef<string | null>(null);
  useEffect(() => {
    ownerResolvedRef.current = null;
  }, [teamId]);
  // The latest draft, read inside the effect WITHOUT subscribing to it — so the
  // resolution runs on roster/hint/me changes, not on every keystroke, while
  // still seeing the user's current owner pick.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  useEffect(() => {
    if (!attendees) return; // roster still loading
    // Once resolved, our queued value is the source of truth for this team
    // (the draft may not have committed yet under Strict Mode's double-invoke).
    const currentOwner =
      ownerResolvedRef.current ?? draftRef.current.ownerProfileId;
    // Keep a still-valid owner (e.g. a manual pick on this team).
    if (currentOwner && attendees.some((a) => a.id === currentOwner)) return;
    // Already auto-resolved for this team → only prune a now-invalid owner;
    // don't re-impose a default over the user's deliberate clear. All ref
    // mutation happens OUT here, never inside a setDraft updater (which React
    // Strict Mode invokes twice and discards the first result of).
    if (ownerResolvedRef.current !== null) {
      if (currentOwner) {
        ownerResolvedRef.current = "";
        setDraft((d) => ({ ...d, ownerProfileId: "" }));
      }
      return;
    }
    const hint = pending.payload.owner_hint?.trim().toLowerCase();
    const hintMatch = hint
      ? (attendees.find((a) => a.display_name.toLowerCase() === hint) ??
         attendees.find((a) => a.display_name.toLowerCase().includes(hint)))
      : undefined;
    const meOnRoster =
      myProfileId && attendees.some((a) => a.id === myProfileId) ? myProfileId : "";
    const resolved = hintMatch?.id ?? meOnRoster;
    ownerResolvedRef.current = resolved;
    if (resolved !== draftRef.current.ownerProfileId) {
      setDraft((d) => ({ ...d, ownerProfileId: resolved }));
    }
  }, [attendees, myProfileId, pending.payload.owner_hint]);

  // Type switch carries title/notes/owner forward (the shared global rule),
  // plus — HERE ONLY — the destination. ``draftForTypeSwitch`` rebuilds from
  // ``emptyDraft`` and deliberately drops ``targetTeamId`` (in the runner the
  // destination is the ambient meeting, and a cascade must not inherit an
  // issue's pick). This host has no ambient meeting: the destination is the
  // user's own first-class pick and submit is gated on it, so dropping it
  // disabled the CTA with no visible reason (the "Create in" picker still
  // showed the default) and flipped the roster query off mid-switch, dropping
  // the owner to Unassigned.
  // ``useCallback`` keeps stable references so ItemSurfaceCreate's children
  // don't re-render on every parent render.
  const changeType = useCallback(
    (type: MeetingItemType) =>
      setDraft((d) => ({ ...draftForTypeSwitch(d, type), targetTeamId: d.targetTeamId })),
    [],
  );
  const change = useCallback(
    (patch: Partial<ItemDraft>) => setDraft((d) => ({ ...d, ...patch })),
    [],
  );

  const cancel = () => {
    pending.reply({ ok: false });
    onClose();
  };

  const submit = async () => {
    if (!teamId || draftError(draft) || !ownerConfirmedOnRoster) return;
    setSaving(true);
    const title = draft.title.trim();
    const notes = draft.description.trim();
    // ``targetTeams`` has no self-exclusion, so it always contains the picked
    // team (``useBroadcastTeams`` filters the picked id OUT — useless here).
    const teamName = targetTeams.find((t) => t.id === teamId)?.name ?? "team";
    try {
      if (draft.type === "issue") {
        await createIssue({
          team_id: teamId,
          title: title.slice(0, 2000), // IssueCreateRequest caps title at 2000
          description: notes || undefined,
          time_horizon: draft.timeHorizon,
          source_type: "kory_artifact",
          owner_id: draft.ownerProfileId || undefined,
          staged: true,
          // Persist the attached artifact so the issue durably wears the body.
          artifact_token: draft.artifact_token,
          artifact_id: draft.artifact_id,
        });
      } else if (draft.type === "todo") {
        await createTodo({
          title,
          owner_profile_id: draft.ownerProfileId,
          due_date: draft.dueDate || null,
          l10_team_id: teamId,
          notes: notes || null,
          // Persist the picked priority (the to-do composer renders the chip).
          priority: draft.priority,
          artifact_token: draft.artifact_token,
          artifact_id: draft.artifact_id,
        });
      } else {
        // headline — title ≤200, body ≤4000. When the title overflows, keep the
        // full text in the body so nothing is lost; if the user also wrote notes,
        // prepend the overflowed title above them rather than dropping either.
        // Cap the combined body at the backend's 4000-char limit.
        const titleOverflow = title.length > 200 ? title : "";
        const body = [titleOverflow, notes].filter(Boolean).join("\n\n").slice(0, 4000);
        await createHeadline({
          team_id: teamId,
          title: title.length > 200 ? title.slice(0, 199) + "…" : title,
          body: body || undefined,
          kind: draft.headlineKind,
          sentiment: draft.sentiment,
          author_id: draft.ownerProfileId || undefined,
          artifact_token: draft.artifact_token,
          artifact_id: draft.artifact_id,
        });
      }
      const noun = ITEM_IDENTITY[draft.type].noun.toLowerCase();
      toast.success(`Added ${noun} to ${teamName}`);
      pending.reply({ ok: true, label: `Added to ${teamName} ✓` });
      onClose();
    } catch {
      toast.error("Couldn't add to team — try again");
      setSaving(false);
    }
  };

  return (
    <ItemSurfaceCreate
      open
      draft={draft}
      attendees={attendees ?? []}
      pending={saving}
      // The destination picker is the NATIVE one ``ItemFields`` owns — the
      // to-do/issue "Create in" picker (writes ``draft.targetTeamId``) and the
      // headline "Post in" picker (pins ``draft.targetTeamId`` as the composing
      // team). Feeding the same server-gated team lists the meeting composer
      // uses keeps the standard ``Owner | Create in`` two-column grid, so no
      // bespoke "Push to" row is needed.
      targetTeams={targetTeams}
      defaultTargetId={defaultTargetId}
      broadcastTeams={broadcastTeams}
      // Same gate as the native composer, plus a destination team and the
      // roster-confirm check (so a stale cross-team owner can't be submitted
      // while the new team's roster is still loading). Passing
      // ``submitDisabled`` overrides the surface's own ``draftError``, so we
      // re-include it here.
      submitDisabled={!teamId || !!draftError(draft) || !ownerConfirmedOnRoster}
      // Opened from an artifact's "Create" action, the draft's ``artifact_token``
      // is already seeded, so ItemTray's attachment-driven tab leads with the
      // Artifact preview; the legacy `<kory-action>` bridge (no token) leads
      // with Notes as before.
      onChangeType={changeType}
      onChange={change}
      onSubmit={submit}
      onClose={cancel}
    />
  );
}

/** Create-from-artifact menu order — Headline leads (the default shape), then
 *  Issue + To-Do; cascade stays hidden (no multi-team L10 yet, matching the
 *  composer's ``MEETING_ITEM_ORDER``). */
const CREATE_FROM_ARTIFACT_TYPES: MeetingItemType[] = ["headline", "issue", "todo"];

/**
 * "＋ Create ▾" — the general create-from-artifact entry point. Turns ANY Kory
 * artifact into a Headline / IDS Issue / To-Do: opens the SAME native item
 * composer ({@link ArtifactItemComposerHost}) with the artifact pre-attached as the
 * body, defaulting to the picked type (Headline first — the menu's top item) and
 * leaving the TypeSwitcher ENABLED so the user can change it. Drive automation is
 * just one feeder for this; the button works on any artifact surface (the viewer
 * chrome bar, the artifacts list row).
 *
 * Reuses the composer verbatim by synthesizing a minimal `PendingAction` — the
 * same shape the `<kory-action>` MessagePort bridge produces — with a no-op
 * `reply` (there's no in-artifact button to reflect the result back to here).
 */
export function CreateFromArtifactButton({
  token,
  id,
  title,
  className,
}: {
  token: string | undefined;
  /** The artifact's id — persisted alongside the token so the writer can
   *  re-publish in place. Optional (the token alone renders the body). */
  id?: string;
  /** The artifact's title — seeds the composer's item title. */
  title: string;
  className?: string;
}) {
  // The seed kind for the composer (which type it opens on). Headline is the
  // menu default; the TypeSwitcher stays enabled so it's changeable after open.
  const [seedKind, setSeedKind] = useState<MeetingItemType | null>(null);
  if (!token) return null;

  const open = (kind: MeetingItemType) => setSeedKind(kind);

  // The composer reads its seed from a `PendingAction`; synthesize one (no
  // in-artifact button ⇒ `reply` is a no-op). `title` on the payload → the item
  // title; the artifact's own title is the fallback.
  const pending: PendingAction | null = seedKind
    ? { id: "create-from-artifact", verb: "l10.add_item", payload: { kind: seedKind }, reply: () => {} }
    : null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {/* The composer's visual language: create is the green primary wherever
              it appears (deliberate — it's the action that turns a document into
              work, and the one action every viewer gets). */}
          <Button size="sm" className={cn("h-8 gap-1 px-2.5 text-xs", className)}>
            <PlusIcon className="h-3.5 w-3.5" />
            Create
            <ChevronDownIcon className="h-3.5 w-3.5 opacity-70" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {/* Headline first — the default create-from-artifact shape — then the
              rest of the composer's type order (cascade stays hidden). */}
          {CREATE_FROM_ARTIFACT_TYPES.map((t) => {
            const Icon = ITEM_IDENTITY[t].icon;
            return (
              <DropdownMenuItem key={t} onSelect={() => open(t)}>
                <Icon className="h-4 w-4" />
                {ITEM_IDENTITY[t].noun}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      {pending && (
        <ArtifactItemComposerHost
          pending={pending}
          artifactTitle={title}
          artifactToken={token}
          artifactId={id}
          onClose={() => setSeedKind(null)}
        />
      )}
    </>
  );
}

function ArtifactDownload({ data }: { data: KoryArtifactView }) {
  const [busy, setBusy] = useState(false);
  const handleDownload = async () => {
    setBusy(true);
    try {
      // Binary-safe: pull the EXACT bytes from the access-gated raw endpoint
      // (?download=1 → attachment). Fall back to inline content only for legacy
      // responses without a content_url (text artifacts, where it's valid).
      const blob = data.content_url
        ? await fetchApiFileBlob(`${data.content_url}?download=1`)
        : new Blob([data.content], { type: data.content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't download this file.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
      <p className="text-sm font-medium">{data.filename}</p>
      <p className="text-xs text-muted-foreground">
        This artifact is a {data.content_type} file.
      </p>
      <Button onClick={handleDownload} disabled={busy} aria-busy={busy}>
        <DownloadIcon className="h-4 w-4 mr-2" />
        {busy ? "Downloading…" : "Download"}
      </Button>
    </div>
  );
}
